/**
 * Closed list of US-style 2-letter jurisdiction codes for PI geography search
 * (project / sample-request search). Server matches case-insensitively; UI sends uppercase codes.
 * Includes states, DC, territories, and common military / postal “state” codes.
 */
const US_JURISDICTION_LABELS: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AS: 'American Samoa',
  AZ: 'Arizona',
  AR: 'Arkansas',
  AA: 'Armed Forces Americas',
  AE: 'Armed Forces Europe / Africa / Canada',
  AP: 'Armed Forces Pacific',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FM: 'Federated States of Micronesia',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MH: 'Marshall Islands',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  MP: 'Northern Mariana Islands',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PW: 'Palau',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VI: 'U.S. Virgin Islands',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export type UsJurisdictionOption = { code: string; label: string };

/** Sorted by label for autocomplete menus. */
export const US_JURISDICTION_OPTIONS: ReadonlyArray<UsJurisdictionOption> = Object.entries(US_JURISDICTION_LABELS)
  .map(([code, label]) => ({ code, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

const BY_CODE_UPPER = new Map<string, UsJurisdictionOption>(
  US_JURISDICTION_OPTIONS.map((o) => [o.code.toUpperCase(), o])
);

/**
 * Resolves a 2-letter jurisdiction code using {@link US_JURISDICTION_OPTIONS} (same source as PI search filters).
 * Unknown codes are shown as is (e.g. whatever state might be in a foreign country); empty values render as an em dash.
 */
export function formatUsJurisdictionForDisplay(code: string | undefined | null): string {
  if (!code?.trim()) return '—';
  const u = code.trim().toUpperCase();
  const o = BY_CODE_UPPER.get(u);
  if (!o) return code;
  return `${o.label} (${o.code})`;
}
